import React, { useState, useCallback, useRef } from 'react';
import { Text, Button, Loading } from '@nextui-org/react';
import { Upload, Document, Delete, Plus } from 'react-iconly';

const FileUpload = ({ defaultName, uploadCb, email = '', acceptedTypes = '.pdf,.jpg,.jpeg,.png' }) => {
  const [state, setState] = useState({
    fileName: '',
    isError: false,
    isUploading: false,
    isDragOver: false
  });
  
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    
    setState(prev => ({ 
      ...prev, 
      fileName: file.name, 
      isError: false 
    }));
    
    uploadCb(file, email)
      .sub({
        start: () => setState(prev => ({ 
          ...prev, 
          isUploading: true, 
          isError: false 
        })),
        end: () => setState(prev => ({ 
          ...prev, 
          isUploading: false 
        })),
        error: () => setState(prev => ({ 
          ...prev, 
          isError: true,
          isUploading: false 
        }))
      });
  }, [uploadCb, email]);

  const handleInputChange = useCallback((event) => {
    const file = event.target.files[0];
    handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setState(prev => ({ ...prev, isDragOver: true }));
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setState(prev => ({ ...prev, isDragOver: false }));
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setState(prev => ({ ...prev, isDragOver: false }));
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      fileName: '', 
      isError: false 
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const getFileIcon = useCallback((fileName) => {
    if (!fileName) return <Upload set="light" size={16} />;
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <Document set="bulk" size={16} primaryColor="#ef4444" />;
    } else if (['jpg', 'jpeg', 'png'].includes(extension)) {
      return <Document set="bulk" size={16} primaryColor="#3b82f6" />;
    } else if (extension === 'csv') {
      return <Document set="bulk" size={16} primaryColor="#10b981" />;
    }
    return <Document set="light" size={16} />;
  }, []);

  const getAcceptedFileTypes = useCallback(() => {
    const types = acceptedTypes.split(',').map(type => type.trim());
    
    // Handle CSV-only case
    if (types.length === 1 && types[0] === '.csv') {
      return 'CSV only';
    }
    
    // Handle other combinations
    if (types.includes('.csv')) {
      return 'PDF, JPG, PNG, CSV';
    }
    
    return 'PDF, JPG, PNG';
  }, [acceptedTypes]);

  const getPlaceholderText = useCallback(() => {
    const types = acceptedTypes.split(',').map(type => type.trim());
    
    // Handle CSV-only case
    if (types.length === 1 && types[0] === '.csv') {
      return 'Choose CSV file';
    }
    
    return 'Choose file';
  }, [acceptedTypes]);

  return (
    <div className="w-full">
      {/* Default name display */}
      {defaultName && (
        <div className="mb-2">
          <Text size={14} className="text-gray-600 font-medium">
            {defaultName}
          </Text>
        </div>
      )}

      {/* Compact File upload area */}
      <div
        className={`
          relative border rounded-lg transition-all duration-200 overflow-hidden
          min-h-[40px]
          ${state.isDragOver 
            ? 'border-blue-400 bg-blue-50' 
            : state.isError 
              ? 'border-red-300 bg-red-50' 
              : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
          }
          ${state.isUploading ? 'pointer-events-none opacity-75' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleInputChange}
          className="hidden"
          accept={acceptedTypes}
          disabled={state.isUploading}
        />

        {/* Compact upload content */}
        {state.isUploading ? (
          <div className="flex items-center justify-center p-3 space-x-2 bg-blue-50">
            <Loading size="sm" color="primary" />
            <div className="text-center">
              <Text size={13} className="font-medium text-blue-700">
                Uploading...
              </Text>
              {state.fileName && (
                <Text size={11} className="text-blue-600 truncate max-w-[150px]">
                  {state.fileName}
                </Text>
              )}
            </div>
          </div>
        ) : state.fileName ? (
          <div className="flex items-center p-3 space-x-2">
            <div className="flex-shrink-0">
              {getFileIcon(state.fileName)}
            </div>
            <div className="flex-1 min-w-0">
              <Text size={13} className="font-medium text-gray-900 truncate">
                {state.fileName}
              </Text>
              <Text size={11} className="text-green-600">
                Ready to upload
              </Text>
            </div>
            <Button
              auto
              light
              size="xs"
              css={{ minWidth: 'auto', width: '24px', height: '24px', padding: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile();
              }}
            >
              <Delete set="light" size={12} />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center p-3 space-x-2">
            <Plus set="light" size={16} className="text-gray-400" />
            <div className="text-left">
              <Text size={13} className="font-medium text-gray-700">
                {getPlaceholderText()}
              </Text>
              <Text size={11} className="text-gray-500">
                {getAcceptedFileTypes()}
              </Text>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {state.isError && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-50 bg-opacity-95">
            <div className="text-center">
              <Text size={12} className="text-red-600 font-medium">
                Upload failed
              </Text>
            </div>
          </div>
        )}
      </div>

      {/* Compact file type hint */}
      <div className="mt-1">
        <Text size={11} className="text-gray-400">
          Max 10MB
        </Text>
      </div>
    </div>
  );
};

export default FileUpload;